import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  BookOpen,
  Plus,
  Upload,
  FileText,
  FileCheck,
  Download,
  PenTool,
} from 'lucide-react';

const steps = [
  {
    title: 'Create a Project',
    description:
      "A project is a folder that organizes your uploaded documents. Click 'New Project' on the Dashboard to create one.",
    badge: (
      <span className="pointer-events-none inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-600 text-white text-xs font-medium rounded-lg">
        <Plus className="w-3.5 h-3.5" />
        New Project
      </span>
    ),
  },
  {
    title: 'Upload Documents',
    description:
      'Open a project and upload PDF documents. You can drag and drop files or use the upload button.',
    badge: (
      <span className="pointer-events-none inline-flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-dark-bg text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border text-xs font-medium rounded-lg">
        <Upload className="w-3.5 h-3.5" />
        Upload Files
      </span>
    ),
  },
  {
    title: 'View a Document',
    description:
      'Click on any document to open the viewer where you can see the PDF and manage redactions. Or select multiple documents using the checkboxes for bulk processing.',
    badge: (
      <span className="pointer-events-none inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-dark-border text-xs font-medium rounded-lg">
        <FileText className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
        document.pdf
      </span>
    ),
  },
  {
    title: 'Analyze & Redact',
    description:
      "Click 'Analyze & Redact' to have AI automatically identify personal information (names, emails, phone numbers, addresses, etc.).",
    badge: (
      <span className="pointer-events-none inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-600 text-white text-xs font-medium rounded-lg">
        <FileCheck className="w-3.5 h-3.5" />
        Analyze & Redact
      </span>
    ),
  },
  {
    title: 'Review & Edit Redactions',
    description:
      'Review detected PII in the sidebar. Remove false positives with the X button, or manually add redactions by drawing boxes or searching keywords.',
    badge: (
      <span className="pointer-events-none inline-flex items-center gap-2">
        <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium rounded">
          NAME
        </span>
        <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium rounded">
          EMAIL
        </span>
        <span className="inline-flex items-center px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-medium rounded">
          PHONE
        </span>
        <PenTool className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
      </span>
    ),
  },
  {
    title: 'Download',
    description:
      "When you're satisfied, click 'Download' to get your redacted PDF. You can choose redaction color, mode, and optionally include a redaction log.",
    badge: (
      <span className="pointer-events-none inline-flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-dark-bg text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border text-xs font-medium rounded-lg">
        <Download className="w-3.5 h-3.5" />
        Download
      </span>
    ),
  },
];

export default function QuickStartGuideModal({ isOpen, onClose }) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              Quick Start Guide
            </span>
          </DialogTitle>
          <DialogDescription>
            Get started with SureDox in 6 easy steps
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
          {steps.map((step, index) => (
            <div key={index} className="flex gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 flex items-center justify-center text-sm font-bold">
                {index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                  {step.description}
                </p>
                <div className="mt-2">{step.badge}</div>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
